import { z } from "zod";
import { AgencyIdSchema, BusinessGraphIdSchema, ProjectIdSchema, RevisionIdSchema } from "../primitives/ids.js";
import { BusinessGraphV1Schema } from "./business-graph.js";
import { PrimaryEvidenceTypeSchema } from "./business-profile-readiness.js";
import { SitePackageIdSchema } from "./site-package.js";
import { StudioProjectModeV1Schema } from "./external-site-proof.js";
/** BusinessGraph drafting lifecycle — not the Studio project room workflow phase (`studio.projectLifecycleView.v1`). */
export const StudioProjectLifecycleStatusV1Schema = z.enum([
    "PROJECT_CREATED",
    "BUSINESSGRAPH_DRAFTING",
    "BUSINESSGRAPH_VALIDATED",
    "REVISION_ACTIVE",
]);
/**
 * PF-004 — canonical StudioProject.status transition matrix.
 * Same-status is allowed (idempotent). No transitions leave REVISION_ACTIVE except re-draft.
 */
const STUDIO_PROJECT_ALLOWED_TRANSITIONS = {
    PROJECT_CREATED: ["BUSINESSGRAPH_DRAFTING"],
    BUSINESSGRAPH_DRAFTING: ["BUSINESSGRAPH_DRAFTING", "BUSINESSGRAPH_VALIDATED"],
    BUSINESSGRAPH_VALIDATED: ["BUSINESSGRAPH_DRAFTING", "REVISION_ACTIVE"],
    REVISION_ACTIVE: ["BUSINESSGRAPH_DRAFTING"],
};
export function canTransitionStudioProjectStatus(from, to) {
    if (from === to) {
        return true;
    }
    return STUDIO_PROJECT_ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}
export function assertStudioProjectTransition(from, to) {
    if (!canTransitionStudioProjectStatus(from, to)) {
        throw new Error(`Invalid studio project lifecycle transition: ${from} → ${to}`);
    }
}
export const StudioProjectLifecycleErrorCodeSchema = z.enum([
    "project_not_found",
    "tenant_mismatch",
    "invalid_transition",
    "stale_lifecycle_version",
    "concurrent_update_conflict",
    "unauthorized_lifecycle",
    "audit_append_failed",
]);
export const StudioProjectLifecycleStateV1Schema = z
    .object({
    schemaId: z.literal("studio.projectLifecycleState.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    status: StudioProjectLifecycleStatusV1Schema,
    lifecycleVersion: z.number().int().min(0),
    updatedAt: z.string().datetime(),
})
    .strict();
export const StudioProjectLifecycleTransitionRequestV1Schema = z
    .object({
    schemaId: z.literal("studio.projectLifecycleTransitionRequest.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    toStatus: StudioProjectLifecycleStatusV1Schema,
    /** Optimistic concurrency token; omit only for trusted system bootstrap paths. */
    expectedLifecycleVersion: z.number().int().min(0).optional(),
    actorRole: z.string().min(1).max(40),
    actorUserId: z.string().min(1).max(120).optional(),
    traceId: z.string().min(1).max(120).optional(),
    correlationId: z.string().min(1).max(120).optional(),
})
    .strict();
export const StudioProjectLifecycleTransitionResultV1Schema = z
    .object({
    schemaId: z.literal("studio.projectLifecycleTransitionResult.v1"),
    ok: z.boolean(),
    state: StudioProjectLifecycleStateV1Schema.optional(),
    error: StudioProjectLifecycleErrorCodeSchema.optional(),
})
    .strict();
export const BusinessGraphDraftStatusV1Schema = z.enum([
    "DRAFT",
    "VALIDATED",
    "ACTIVATED",
    "REJECTED",
    "SUPERSEDED",
]);
/** AG-002 — explicit project builder path captured at create time. */
export const StudioProjectBuilderPathV1Schema = z.enum(["ENREIN_GENERATE", "EXTERNAL_SUPPORTED"]);
/** AG-002 delivery inputs required on project create. */
export const ProjectCreateDeliveryInputsV1Schema = z
    .object({
    clientEntityName: z.string().min(1).max(160),
    scopeSummary: z.string().min(1).max(2000),
    ownerLabel: z.string().min(1).max(160),
    builderPath: StudioProjectBuilderPathV1Schema,
    sitePackageId: SitePackageIdSchema,
    targetOutcome: z.string().min(1).max(2000),
})
    .strict();
export const StudioBusinessIdSchema = z.string().min(1).max(80);
export const ProjectCreateRequestV1Schema = z
    .object({
    schemaId: z.literal("project.createRequest.v1"),
    projectId: ProjectIdSchema.optional(),
    businessName: z.string().min(1).max(160),
    projectMode: StudioProjectModeV1Schema.optional(),
    clientEntityName: z.string().min(1).max(160),
    scopeSummary: z.string().min(1).max(2000),
    ownerLabel: z.string().min(1).max(160),
    builderPath: StudioProjectBuilderPathV1Schema,
    sitePackageId: SitePackageIdSchema,
    targetOutcome: z.string().min(1).max(2000),
    /** Bind to an existing StudioBusiness, or omit to create one from display fields. */
    businessId: StudioBusinessIdSchema.optional(),
})
    .strict();
/** Test/fixture helper — full AG-002 create payload with conservative defaults. */
export function projectCreateRequestV1(input) {
    return ProjectCreateRequestV1Schema.parse({
        schemaId: "project.createRequest.v1",
        projectId: input.projectId,
        businessName: input.businessName,
        projectMode: input.projectMode,
        clientEntityName: input.clientEntityName ?? input.businessName,
        scopeSummary: input.scopeSummary ?? `Initial scope for ${input.businessName}`,
        ownerLabel: input.ownerLabel ?? "Agency owner",
        builderPath: input.builderPath ?? "ENREIN_GENERATE",
        sitePackageId: input.sitePackageId ?? "package-standard-5",
        targetOutcome: input.targetOutcome ?? "Launch an approved client website revision",
        businessId: input.businessId,
    });
}
export const ProjectCreateResultV1Schema = z
    .object({
    schemaId: z.literal("project.createResult.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    projectStatus: StudioProjectLifecycleStatusV1Schema,
    projectMode: StudioProjectModeV1Schema,
    businessName: z.string().min(1).max(160),
    clientEntityName: z.string().min(1).max(160),
    scopeSummary: z.string().min(1).max(2000),
    ownerLabel: z.string().min(1).max(160),
    builderPath: StudioProjectBuilderPathV1Schema,
    sitePackageId: SitePackageIdSchema,
    targetOutcome: z.string().min(1).max(2000),
    createdAt: z.string().datetime(),
    businessId: StudioBusinessIdSchema.optional(),
})
    .strict();
const IntakeServiceAreaSchema = z.object({ id: z.string().min(1).optional(), name: z.string().min(1).max(120) }).strict();
const IntakeServiceSchema = z.object({ id: z.string().min(1).optional(), name: z.string().min(1).max(120) }).strict();
export const BusinessGraphDraftCreateRequestV1Schema = z
    .object({
    schemaId: z.literal("businessgraph.draftCreateRequest.v1"),
    businessName: z.string().min(1).max(160),
    primaryPhone: z.string().max(80).optional(),
    primaryEmail: z.string().email().optional(),
    description: z.string().max(2000).optional(),
    primaryEvidenceType: PrimaryEvidenceTypeSchema.optional(),
    serviceAreas: z.array(IntakeServiceAreaSchema).max(25).default([]),
    services: z.array(IntakeServiceSchema).max(50).default([]),
    sitePackageId: SitePackageIdSchema.optional(),
    lensId: z.string().min(1).optional(),
})
    .strict();
export const BusinessGraphDraftUpdateV1Schema = z
    .object({
    schemaId: z.literal("businessgraph.draftUpdate.v1"),
    businessName: z.string().min(1).max(160).optional(),
    primaryPhone: z.string().max(80).nullable().optional(),
    primaryEmail: z.string().email().nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
    primaryEvidenceType: PrimaryEvidenceTypeSchema.nullable().optional(),
    serviceAreas: z.array(IntakeServiceAreaSchema).max(25).optional(),
    services: z.array(IntakeServiceSchema).max(50).optional(),
    sitePackageId: SitePackageIdSchema.optional(),
    lensId: z.string().min(1).optional(),
})
    .strict();
export const BusinessGraphValidationIssueV1Schema = z
    .object({
    path: z.string().min(1),
    code: z.string().min(1),
    message: z.string().min(1),
})
    .strict();
export const BusinessGraphValidationResultV1Schema = z
    .object({
    schemaId: z.literal("businessgraph.validationResult.v1"),
    valid: z.boolean(),
    checkedAt: z.string().datetime(),
    issues: z.array(BusinessGraphValidationIssueV1Schema),
})
    .strict();
export const BusinessGraphDraftViewV1Schema = z
    .object({
    schemaId: z.literal("businessgraph.draftView.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    draftId: z.string().min(1),
    status: BusinessGraphDraftStatusV1Schema,
    businessGraphId: BusinessGraphIdSchema,
    revisionId: RevisionIdSchema,
    businessName: z.string().min(1),
    primaryPhone: z.string().optional(),
    primaryEmail: z.string().optional(),
    serviceAreaCount: z.number().int().min(0),
    serviceCount: z.number().int().min(0),
    validation: BusinessGraphValidationResultV1Schema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
/**
 * Studio draft "activate" accept result.
 * Before worker QA success this is candidate/queued (no activatedAt).
 * activatedAt is present only when the revision is already the active approved head
 * (idempotent replay after worker activation).
 */
export const BusinessGraphActivationResultV1Schema = z
    .object({
    schemaId: z.literal("businessgraph.activationResult.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    draftId: z.string().min(1),
    revisionId: RevisionIdSchema,
    snapshotHash: z.string().regex(/^[0-9a-f]{64}$/),
    /** Present only after active-head advancement (worker QA path or idempotent replay). */
    activatedAt: z.string().datetime().optional(),
    /** Accept-path state when candidate is queued for generation + QA (not yet active). */
    processingStatus: z.enum(["CANDIDATE_QUEUED"]).optional(),
    /** Generation run created for the candidate (when processingStatus is CANDIDATE_QUEUED). */
    runId: z.string().min(1).optional(),
})
    .strict();
export const StudioBusinessGraphStatusV1Schema = z
    .object({
    schemaId: z.literal("studio.businessGraphStatus.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    projectStatus: StudioProjectLifecycleStatusV1Schema,
    currentBusinessGraphRevisionId: RevisionIdSchema.nullable(),
    latestDraftId: z.string().min(1).nullable(),
    latestDraftStatus: BusinessGraphDraftStatusV1Schema.nullable(),
    updatedAt: z.string().datetime(),
})
    .strict();
/** Active BusinessGraph revision for Studio generation (Phase 1). */
export const BusinessGraphActiveViewV1Schema = z
    .object({
    schemaId: z.literal("businessgraph.activeView.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    revisionId: RevisionIdSchema,
    businessGraph: BusinessGraphV1Schema,
    activatedAt: z.string().datetime(),
})
    .strict();
export function validateBusinessGraphForActivation(graph, checkedAt = new Date().toISOString()) {
    const parsed = BusinessGraphV1Schema.safeParse(graph);
    if (parsed.success) {
        return BusinessGraphValidationResultV1Schema.parse({
            schemaId: "businessgraph.validationResult.v1",
            valid: true,
            checkedAt,
            issues: [],
        });
    }
    return BusinessGraphValidationResultV1Schema.parse({
        schemaId: "businessgraph.validationResult.v1",
        valid: false,
        checkedAt,
        issues: parsed.error.issues.map((issue) => ({
            path: issue.path.join(".") || "root",
            code: issue.code,
            message: issue.message,
        })),
    });
}
//# sourceMappingURL=studio-project-workflow.js.map